import { useNavigate } from "react-router-dom";
import {
  Anchor,
  Button,
  Card,
  Group,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { CompositeChart } from "@mantine/charts";
import { useQuery } from "@tanstack/react-query";
import {
  IconArrowRight,
  IconCash,
  IconClockHour4,
  IconPercentage,
  IconRoute,
  IconUserCheck,
  IconAlertTriangle,
  IconCar,
  IconSearch,
  IconMapPin,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { consoleApi } from "../../api/console";
import { EMPTY_MONEY } from "../../lib/money";
import { fillDays } from "../../lib/window";
import { useAuth } from "../../auth/useAuth";
import { can } from "../../auth/rbac";
import { adminRoleLabel } from "../../types/admin";
import { useI18n } from "../../i18n/context";
import { VehicleIcon } from "../../components/brand/VehicleIcon";
import { FOCUS_SEARCH_EVENT } from "../../components/layout/HeaderSearch";
import { KpiCard, KpiGroup } from "../../components/console/KpiCard";
import { InboxList } from "../../components/console/InboxList";
import { QueryError } from "../../components/common/QueryError";
import { formatCompact, formatMoney, formatNumber, formatPercent } from "../../lib/format";

/**
 * Role-aware home. Every widget is gated on a permission, so the CEO sees the
 * business, the COO/ops see the road, registration sees the pipeline and
 * support sees queues — from one page, without separate dashboards to keep
 * in sync.
 */
export function HomePage() {
  const { admin } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const role = admin?.role;

  const canLive = can(role, "live.view");
  const canFinance = can(role, "finance.view");
  const canDash = can(role, "dashboard.view");

  const today = useQuery({ queryKey: ["ops-today"], queryFn: consoleApi.today, enabled: canDash, refetchInterval: 60_000 });
  const inbox = useQuery({ queryKey: ["ops-inbox"], queryFn: consoleApi.inbox, enabled: canDash, refetchInterval: 60_000 });
  const live = useQuery({ queryKey: ["ops-live"], queryFn: consoleApi.live, enabled: canLive, refetchInterval: 20_000 });
  const finance = useQuery({
    queryKey: ["finance-home"],
    queryFn: () =>
      consoleApi.finance({
        from: dayjs().subtract(13, "day").startOf("day").toISOString(),
        to: dayjs().toISOString(),
        groupBy: "day",
      }),
    enabled: canFinance,
  });

  const hour = new Date().getHours();
  const greeting = hour < 12 ? t("home.morning") : hour < 17 ? t("home.afternoon") : t("home.evening");
  const d = today.data;
  const vsY = t("home.vsYesterday");

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <div>
          <Title order={2}>
            {greeting}, {admin?.name?.split(" ")[0] ?? ""}
          </Title>
          <Text c="dimmed" size="sm">
            {dayjs().format("dddd, D MMMM YYYY")} · {adminRoleLabel(role)}
          </Text>
        </div>
        <Group gap="xs">
          {canLive && (
            <Button leftSection={<IconMapPin size={16} />} onClick={() => navigate("/live")}>
              {t("home.openLive")}
            </Button>
          )}
          <Button
            variant="default"
            leftSection={<IconSearch size={16} />}
            onClick={() => window.dispatchEvent(new Event(FOCUS_SEARCH_EVENT))}
          >
            {t("home.find")}
          </Button>
        </Group>
      </Group>

      {canDash && (
        <>
          {today.error && <QueryError error={today.error} onRetry={today.refetch} />}
          <KpiGroup cols={{ base: 2, md: 3, xl: canFinance ? 6 : 4 }}>
            <KpiCard
              loading={today.isLoading}
              label={t("home.kpi.requests")}
              icon={IconRoute}
              value={formatNumber(d?.today.requests)}
              current={d?.today.requests}
              previous={d?.yesterdaySameTime.requests}
              compareLabel={vsY}
            />
            <KpiCard
              loading={today.isLoading}
              label={t("home.kpi.completed")}
              icon={IconUserCheck}
              value={formatNumber(d?.today.completed)}
              current={d?.today.completed}
              previous={d?.yesterdaySameTime.completed}
              compareLabel={vsY}
            />
            <KpiCard
              loading={today.isLoading}
              label={t("home.kpi.fulfilment")}
              icon={IconPercentage}
              value={formatPercent(d?.today.fulfilmentRate)}
              current={d?.today.fulfilmentRate}
              previous={d?.yesterdaySameTime.fulfilmentRate}
              compareLabel={vsY}
              hint={t("home.kpi.fulfilmentHint")}
            />
            <KpiCard
              loading={today.isLoading}
              label={t("home.kpi.timeToAccept")}
              icon={IconClockHour4}
              value={d?.today.avgSecondsToAccept != null ? `${d.today.avgSecondsToAccept}s` : "—"}
              current={d?.today.avgSecondsToAccept}
              previous={d?.yesterdaySameTime.avgSecondsToAccept}
              goodWhen="down"
              compareLabel={vsY}
            />
            {canFinance && (
              <>
                <KpiCard
                  loading={today.isLoading}
                  label={t("home.kpi.riderPayments")}
                  icon={IconCash}
                  value={formatMoney(d?.today.gmv)}
                  current={d?.today.gmv}
                  previous={d?.yesterdaySameTime.gmv}
                  compareLabel={vsY}
                />
                <KpiCard
                  loading={today.isLoading}
                  label={t("home.kpi.safiriEarned")}
                  icon={IconCash}
                  value={formatMoney(d?.today.platformEarning)}
                  current={d?.today.platformEarning}
                  previous={d?.yesterdaySameTime.platformEarning}
                  compareLabel={vsY}
                />
              </>
            )}
          </KpiGroup>
        </>
      )}

      <SimpleGrid cols={{ base: 1, lg: canLive || canFinance ? 2 : 1 }} spacing="lg">
        {canDash && (
          <Card padding="lg">
            <Group justify="space-between" mb="sm">
              <Text fw={650}>{t("home.needsAttention")}</Text>
              <Anchor size="sm" onClick={() => navigate("/inbox")}>
                {t("inbox.viewAll")} <IconArrowRight size={12} />
              </Anchor>
            </Group>
            {inbox.isLoading ? <Skeleton h={220} /> : inbox.data && <InboxList sections={inbox.data.sections} compact />}
          </Card>
        )}

        <Stack gap="lg">
          {canLive && (
            <Card padding="lg">
              <Group justify="space-between" mb="sm">
                <Text fw={650}>{t("home.rightNow")}</Text>
                <Anchor size="sm" onClick={() => navigate("/live")}>
                  {t("home.openLive")} <IconArrowRight size={12} />
                </Anchor>
              </Group>
              {live.isLoading || !live.data ? (
                <Skeleton h={130} />
              ) : (
                <SimpleGrid cols={{ base: 2, sm: 4 }}>
                  <Mini label={t("live.searching")} value={live.data.summary.searching} />
                  <Mini label={t("live.onTrip")} value={live.data.summary.onTrip + live.data.summary.toPickup + live.data.summary.atPickup} />
                  <Mini label={t("live.idleDrivers")} value={live.data.summary.driversIdle} icon={IconCar} />
                  <Mini
                    label={t("live.stuck")}
                    value={live.data.summary.stuck}
                    icon={IconAlertTriangle}
                    danger={live.data.summary.stuck > 0}
                    onClick={() => navigate("/live?filter=stuck")}
                  />
                </SimpleGrid>
              )}
              {live.data && live.data.byRideType.length > 0 && (
                <Stack gap={4} mt="md">
                  {live.data.byRideType.map((r) => (
                    <Group key={r.id} justify="space-between">
                      <Group gap={8} wrap="nowrap">
                        <VehicleIcon rideType={r.name} size={34} />
                        <Text size="sm">{r.name}</Text>
                      </Group>
                      <Text size="sm" c="dimmed" className="num">
                        {t("home.supplyLine", { idle: r.idle, busy: r.onTrip, waiting: r.searching })}
                      </Text>
                    </Group>
                  ))}
                </Stack>
              )}
            </Card>
          )}

          {canFinance && (
            <Card padding="lg">
              <Group justify="space-between" mb="sm">
                <Text fw={650}>{t("home.last14")}</Text>
                <Anchor size="sm" onClick={() => navigate("/finance")}>
                  {t("nav.finance")} <IconArrowRight size={12} />
                </Anchor>
              </Group>
              {finance.isLoading || !finance.data ? (
                <Skeleton h={180} />
              ) : (
                <>
                  <CompositeChart
                    h={170}
                    data={fillDays(finance.data.series, "day", finance.data.window.from, finance.data.window.to, (bucket) => ({ ...EMPTY_MONEY, bucket })).map((s) => ({
                      day: dayjs(s.bucket).format("D MMM"),
                      riderPaid: s.riderPaid,
                      safiriEarned: s.platformEarning,
                    }))}
                    dataKey="day"
                    // Series names are ids (no spaces): Mantine derives SVG
                    // gradient ids from them. Display text goes in `label`.
                    series={[
                      { name: "riderPaid", label: t("fin.riderPaid"), color: "var(--viz-cat-2)", type: "bar" },
                      { name: "safiriEarned", label: t("fin.safiriEarned"), color: "var(--viz-cat-1)", type: "line" },
                    ]}
                    curveType="monotone"
                    valueFormatter={(v) => formatCompact(v)}
                    withLegend
                    withDots={false}
                    legendProps={{ verticalAlign: "bottom" }}
                    gridAxis="y"
                    tickLine="none"
                    maxBarWidth={24}
                  />
                  <SimpleGrid cols={2} mt="md">
                    <Mini label={t("fin.receivables")} value={formatMoney(finance.data.positions.driverReceivables)} />
                    <Mini label={t("fin.payables")} value={formatMoney(finance.data.positions.driverPayables)} />
                  </SimpleGrid>
                </>
              )}
            </Card>
          )}
        </Stack>
      </SimpleGrid>
    </Stack>
  );
}

function Mini({
  label,
  value,
  icon: Icon,
  danger,
  onClick,
}: {
  label: string;
  value: string | number;
  icon?: typeof IconCar;
  danger?: boolean;
  onClick?: () => void;
}) {
  return (
    <Stack
      gap={2}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      style={{ cursor: onClick ? "pointer" : undefined }}
    >
      <Text size="sm" c="var(--text-secondary)" fw={500}>
        {label}
      </Text>
      <Group gap={4}>
        {Icon && <Icon size={16} color={danger ? "var(--error-fg)" : undefined} aria-hidden />}
        <Text fw={700} fz="xl" c={danger ? "var(--error-fg)" : undefined} className="num">
          {value}
        </Text>
      </Group>
    </Stack>
  );
}
